
const {URL} = require("../../../services/http.service")
const TRACKING_KEY = "db_tracking";
const EVENTS_KEY = "db_events";
const MAX_RETRY = 50;
const FAIL_KEY = "tracking_fail_count";
const META_PIXEL_CACHE_PREFIX = "db_meta_pixel_";

const inferBusinessUuidFromPath = () => {
  if (typeof window === "undefined") return "";
  const parts = window.location.pathname.split("/").filter(Boolean);
  const storeIndex = parts.indexOf("store");
  if (storeIndex >= 0 && parts[storeIndex + 1]) return parts[storeIndex + 1];
  return "";
};

const resolveBusinessUuid = (candidate = "") => {
  if (candidate) return candidate;

  try {
    const trackingRaw = localStorage.getItem(TRACKING_KEY);
    if (trackingRaw) {
      const parsed = JSON.parse(trackingRaw);
      if (parsed?.businessUuid) return parsed.businessUuid;
    }
  } catch (_) {}

  return inferBusinessUuidFromPath();
};

const getCachedPixelId = (businessUuid) => {
  if (!businessUuid) return "";
  return localStorage.getItem(`${META_PIXEL_CACHE_PREFIX}${businessUuid}`) || "";
};

const setCachedPixelId = (businessUuid, pixelId) => {
  if (!businessUuid || !pixelId) return;
  localStorage.setItem(`${META_PIXEL_CACHE_PREFIX}${businessUuid}`, String(pixelId));
};

const loadMetaPixelScript = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.fbq) return resolve(true);

    const bootstrapMetaPixel = function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    };

    bootstrapMetaPixel(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

    resolve(true);
  });

const initMetaPixel = async (pixelId) => {
  if (!pixelId || typeof window === "undefined") return false;
  await loadMetaPixelScript();
  if (!window.fbq) return false;

  const normalized = String(pixelId).trim();
  window.__dbMetaPixelIds = window.__dbMetaPixelIds || new Set();
  if (!window.__dbMetaPixelIds.has(normalized)) {
    window.fbq("init", normalized);
    window.__dbMetaPixelIds.add(normalized);
  }
  return true;
};

const queueMetaEvent = async (businessUuid, eventName, payload = {}) => {
  try {
    const resolvedBusinessUuid = resolveBusinessUuid(businessUuid);
    await ensureMetaPixelForBusiness(resolvedBusinessUuid);
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", eventName, payload);
    }
  } catch (err) {
    console.warn("Meta event dispatch skipped:", err?.message || err);
  }
};

export const ensureMetaPixelForBusiness = async (businessUuid) => {
  const resolvedBusinessUuid = resolveBusinessUuid(businessUuid);
  if (!resolvedBusinessUuid) return "";

  const cached = getCachedPixelId(resolvedBusinessUuid);
  if (cached) {
    await initMetaPixel(cached);
    return cached;
  }

  try {
    const response = await fetch(`${URL}/store-settings/${resolvedBusinessUuid}/meta/public-config`);
    if (!response.ok) return "";
    const parsed = await response.json();
    const pixelId = parsed?.data?.pixelId || "";
    if (!pixelId) return "";
    setCachedPixelId(resolvedBusinessUuid, pixelId);
    await initMetaPixel(pixelId);
    return pixelId;
  } catch (error) {
    return "";
  }
};

export const trackMetaViewContent = ({
  businessUuid,
  contentId,
  contentName,
  value = 0,
  currency = "INR",
} = {}) => {
  if (!businessUuid || !contentId) return;
  queueMetaEvent(businessUuid, "ViewContent", {
    content_ids: [String(contentId)],
    content_type: "product",
    content_name: contentName || "Product",
    value: Number(value || 0),
    currency,
  });
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[2]) : "";
};

const detectChannel = ({ utmSource, referrer, fbclid }) => {
  if (fbclid) return "meta";
  const value = `${utmSource || ""} ${referrer || ""}`.toLowerCase();
  if (/facebook|instagram|meta/.test(value)) return "meta";
  return "organic";
};

const buildSourcePayload = () => {
  const params = new URLSearchParams(window.location.search);
  const fbclid = params.get("fbclid") || "";
  const fbp = getCookie("_fbp") || "";
  const fbc = fbclid ? `fb.1.${Date.now()}.${fbclid}` : getCookie("_fbc") || "";
  const utmSource = params.get("utm_source") || "";

  return {
    landingUrl: window.location.href,
    landingPath: window.location.pathname,
    utmSource,
    utmMedium: params.get("utm_medium") || "",
    utmCampaign: params.get("utm_campaign") || "",
    utmContent: params.get("utm_content") || "",
    utmTerm: params.get("utm_term") || "",
    fbclid,
    fbp,
    fbc,
    channel: detectChannel({ utmSource, referrer: document.referrer, fbclid }),
  };
};

const getCanvasFingerprint = () => {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText("DukanBuddy, 💼🚀 tracking", 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText("DukanBuddy, 💼🚀 tracking", 4, 17);
    return canvas.toDataURL();
  } catch (e) {
    return null;
  }
};

const getWebGLInfo = () => {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl") || canvas.getContext("experimental-webgl");
    if (!gl) return { renderer: "unknown", vendor: "unknown" };
    const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
    return {
      renderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
      vendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR),
    };
  } catch (e) {
    return { renderer: "error", vendor: "error" };
  }
};

const getAudioFingerprint = () => {
  // Simple check for audio context support
  return !!(window.AudioContext || window.webkitAudioContext);
};

const generateHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return hash.toString(16);
};

export const initTracking = async (businessUuid) => {
  const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

  const existingRaw = localStorage.getItem(TRACKING_KEY);
  let shouldCreateNew = true;

  if (existingRaw) {
    const existing = JSON.parse(existingRaw);
    const lastVisit = new Date(existing.firstVisitAt).getTime();
    const now = Date.now();

    if (now - lastVisit < SESSION_TIMEOUT) {
      shouldCreateNew = false;
    }
  }

  if (!shouldCreateNew) return;

  const webgl = getWebGLInfo();
  const canvasFP = getCanvasFingerprint();
  const ua = navigator.userAgent;
  
  // Basic OS Detection
  let osName = "Unknown";
  if (ua.indexOf("Windows") !== -1) osName = "Windows";
  else if (ua.indexOf("Android") !== -1) osName = "Android";
  else if (ua.indexOf("iPhone") !== -1 || ua.indexOf("iPad") !== -1 || ua.indexOf("iPod") !== -1) osName = "iOS";
  else if (ua.indexOf("Mac") !== -1) osName = "MacOS";
  else if (ua.indexOf("Linux") !== -1) osName = "Linux";

  // Basic Browser Detection
  let browserName = "Unknown";
  if (ua.indexOf("Chrome") !== -1) browserName = "Chrome";
  else if (ua.indexOf("Safari") !== -1) browserName = "Safari";
  else if (ua.indexOf("Firefox") !== -1) browserName = "Firefox";
  else if (ua.indexOf("Edge") !== -1) browserName = "Edge";

  const data = {
    sessionId: "sess_" + crypto.randomUUID(),
    businessUuid,
    firstVisitAt: new Date().toISOString(),
    referrer: document.referrer || "direct",

    // 1. Device Identification
    source: buildSourcePayload(),
    deviceId: generateHash(ua + canvasFP + window.screen.width),
    deviceType: /Mobile|Android|iPhone|iPad/i.test(ua) ? "mobile" : "desktop",
    deviceBrand: navigator.vendor || "Unknown",
    deviceModel: "Unknown",
    deviceManufacturer: navigator.vendor || "Unknown",
    deviceArchitecture: navigator.platform,
    cpuClass: navigator.cpuClass || "Unknown",
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: navigator.deviceMemory || 0,

    // 2. Operating System
    osName,
    osVersion: "Unknown", 
    osBuildNumber: "Unknown",
    platformType: navigator.platform,
    systemLanguage: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,

    // 3. Browser Fingerprint
    browserName,
    browserVersion: navigator.appVersion,
    userAgentString: ua,
    browserEngine: navigator.product,
    cookiesEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || "unknown",
    plugins: Array.from(navigator.plugins).map(p => p.name),
    webglRenderer: webgl.renderer,
    webglVendor: webgl.vendor,
    canvasFingerprint: canvasFP ? generateHash(canvasFP) : null,
    audioFingerprint: getAudioFingerprint() ? "supported" : "not-supported",

    // 4. Screen & Display
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    availableScreenResolution: `${window.screen.availWidth}x${window.screen.availHeight}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,
    screenOrientation: (window.screen.orientation && window.screen.orientation.type) || "Unknown",
    touchSupport: ("ontouchstart" in window || navigator.maxTouchPoints > 0),
    maxTouchPoints: navigator.maxTouchPoints || 0,

    // 5. Network (Partial)
    connectionType: navigator.connection ? navigator.connection.effectiveType : "Unknown",
    
    // 6. Location Signals
    timezoneOffset: new Date().getTimezoneOffset(),
    gpsAvailable: "geolocation" in navigator,

    // Legacy support
    device: {
      isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua),
      platform: navigator.platform,
      userAgent: ua.slice(0, 100),
      screen: `${window.innerWidth}x${window.innerHeight}`,
      language: navigator.language,
    },
  };

  // Modern Device Info via UserAgentData
  if (navigator.userAgentData && navigator.userAgentData.getHighEntropyValues) {
    try {
      const hints = await navigator.userAgentData.getHighEntropyValues([
        "model",
        "platform",
        "platformVersion",
        "architecture",
        "bitness",
      ]);
      data.deviceModel = hints.model || data.deviceModel;
      data.osName = hints.platform || data.osName;
      data.osVersion = hints.platformVersion || data.osVersion;
      data.deviceArchitecture = hints.architecture || data.deviceArchitecture;
    } catch (e) {
      console.warn("UA Data failed", e);
    }
  }

  localStorage.setItem(TRACKING_KEY, JSON.stringify(data));
};

export const getTrackingContext = () => {
  const raw = localStorage.getItem(TRACKING_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return {
      sessionId: parsed.sessionId || "",
      referrer: parsed.referrer || document.referrer || "",
      ...(parsed.source || {}),
    };
  } catch (_) {
    return null;
  }
};
export const trackIntent = (intent) => {
  const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");

  events.push({
    type: "INTENT",
    intent, // B2B / B2C
    at: new Date().toISOString(),
  });

  localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
};
export const trackPageView = (page) => {
  const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");
  events.push({
    type: "PAGE_VIEW",
    page: page || window.location.pathname,
    at: new Date().toISOString(),
  });
  localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
};

export const trackAddToCart = (productOrPayload, businessUuidArg) => {
  const payload =
    typeof productOrPayload === "object" && productOrPayload !== null
      ? productOrPayload
      : { contentId: productOrPayload, businessUuid: businessUuidArg };

  const businessUuid = resolveBusinessUuid(payload.businessUuid || businessUuidArg);
  const contentId = payload.productId || payload.contentId || payload._id || "";
  const contentName = payload.contentName || payload.productName || "Product";
  const value = Number(payload.value || 0);
  const currency = payload.currency || "INR";

  const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");
  events.push({
    type: "ADD_TO_CART",
    productId: contentId,
    businessUuid,
    at: new Date().toISOString(),
  });
  localStorage.setItem(EVENTS_KEY, JSON.stringify(events));

  if (businessUuid && contentId) {
    queueMetaEvent(businessUuid, "AddToCart", {
      content_ids: [String(contentId)],
      content_type: "product",
      content_name: contentName,
      value,
      currency,
    });
  }
};

export const trackCartQuantityChange = ({
  productId,
  businessUuid,
  action,
  quantity,
  productName,
}) => {
  const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");

  events.push({
    type: "CART_QTY_CHANGE",
    action, // INCREMENT | DECREMENT
    productId,
    businessUuid,
    quantity,
    productName,
    at: new Date().toISOString(),
  });

  localStorage.setItem(EVENTS_KEY, JSON.stringify(events));
};

export const trackPurchase = ({ orderId, amount, businessUuid, items = [], currency = "INR" }) => {
  const events = JSON.parse(localStorage.getItem(EVENTS_KEY) || "[]");
  events.push({
    type: "PURCHASE",
    orderId,
    amount,
    at: new Date().toISOString(),
  });
  localStorage.setItem(EVENTS_KEY, JSON.stringify(events));

  const contentIds = items
    .map((item) => item?.productId || item?._id)
    .filter(Boolean)
    .map((id) => String(id));

  const resolvedBusinessUuid = resolveBusinessUuid(businessUuid);

  if (resolvedBusinessUuid) {
    queueMetaEvent(resolvedBusinessUuid, "Purchase", {
      content_ids: contentIds,
      content_type: "product",
      value: Number(amount || 0),
      currency,
    });
  }
};

export const syncTracking = (reason = "manual") => {
  const trackingRaw = localStorage.getItem(TRACKING_KEY);
  const eventsRaw = localStorage.getItem(EVENTS_KEY);

  if (!trackingRaw || !eventsRaw) {
    console.log("❌ No data to sync");
    return;
  }

  const tracking = JSON.parse(trackingRaw);
  const events = JSON.parse(eventsRaw);

  if (!Array.isArray(events) || events.length === 0) {
    console.log("⏭️ No events - skip");
    return;
  }
const failCount = Number(localStorage.getItem(FAIL_KEY) || 0);
  if (failCount >= MAX_RETRY) {
    console.warn("🚫 Tracking disabled (max retries reached)");
    return;
  }
  const payload = { tracking, events, reason };

  fetch(`${URL}/tracking/guest`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  })
    .then((res) => {
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
})
    .then((data) => {
      if (data.success) {
        localStorage.removeItem(EVENTS_KEY);
        localStorage.removeItem(FAIL_KEY);
      }
    })
    .catch((err) => {
      console.error("❌ SYNC FAILED:", err);
      localStorage.setItem(FAIL_KEY, failCount + 1);
    });
};

// document.addEventListener("DOMContentLoaded", () => {
//   // Get businessUuid from URL or data attribute
//   const businessUuid =
//     new URLSearchParams(window.location.search).get("business") ||
//     document.body.dataset.businessUuid ||
//     "8995d301-95df-400b-98cf-2db64ac6bff3"; // fallback

//   initTracking(businessUuid);
//   trackPageView(window.location.pathname);

//   // Track every 10 minutes if events exist
//   setInterval(() => {
//     const eventsRaw = localStorage.getItem(EVENTS_KEY);
//     if (eventsRaw && JSON.parse(eventsRaw).length > 0) {
//       syncTracking("interval");
//     }
//   }, 10 * 60 * 1000);
// });

// Page unload tracking
window.addEventListener("beforeunload", () => {
  syncTracking("unload");
});
